import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useLocalStorage } from 'react-use';
import { withAnalytic } from '@skooldio/analytics-provider';

import { withConfig, useConfig } from 'Context/ConfigContext';
import Slider from 'Components/Slider';
import { Flex, Box, Text, SvgIcon } from 'Components/Base';
import { WireSyncProvider, ROLES, useWireSync } from 'Util/WireSync';
import { genSliderEvent, SLIDER_ACTIONS } from 'Util/Analytics';
import { useClassroomContext } from 'Context/ClassroomContext';
import { useComponentSize } from 'Util/Hooks';

import VideoBox from '../VideoBox';
import { MATERIAL_TYPE, VIDEO_PROVIDER } from '../constant';
import { useVideoPlayer } from '../../../../Context/VideoPlayer';

import { Slide, SlideWithFullScreen, VideoSlideLayout } from './components';

const COMPONENT_SYNC_KEY = {
  VIDEO: 'VIDEO',
  SLIDE: 'SLIDE',
};

const getVideoSize = ({ width }) => {
  if (width <= 225) return 'size-small';
  else if (width <= 390 && width > 225) return 'size-medium';
  return 'size-large';
};

const Video = withConfig(
  ({
    videoAsset,
    markers,
    appConfig: {
      videoPlayer: { showVideoOverlayOnReady, showVideoOverlayOnPause, showVideoOverlayOnEnd },
    },
    syncKey,
    sliderValue,
    ...videoProps
  }) => {
    const { player } = useVideoPlayer();
    const { role, setValue, reset, node, ROLES } = useWireSync(syncKey);
    const videoBoxWrapperRef = useRef();
    const videoBoxWrapperSize = useComponentSize(videoBoxWrapperRef);

    const setCurrentTime = useCallback(setValue, []);

    useEffect(() => {
      if (player) {
        player.on('timeupdate', () => {
          const videoDuration = player?.duration();
          const currentTime = player?.currentTime();
          if (currentTime !== 0 && currentTime !== videoDuration) {
            setCurrentTime(currentTime, ROLES.MASTER);
          }
        });
      }
    }, [player, setCurrentTime]);

    useEffect(() => {
      if (player) {
        player.markers.reset(markers);
      }
    }, [player, markers]);

    return (
      <Box
        className={`responsive-width ${getVideoSize(videoBoxWrapperSize)}`}
        display={sliderValue === 1 ? 'none' : 'block'}
        width={1}
        ref={videoBoxWrapperRef}
      >
        <VideoBox
          title={videoAsset?.title}
          type={videoAsset?.type}
          value={videoAsset}
          isVideoJsControl
          shouldHideControlOnSmallSize={videoBoxWrapperSize.width <= 100}
          {...videoProps}
        />
      </Box>
    );
  }
);

const VARIANTS = {
  EQUAL: 'EQUAL',
  FLUID: 'FLUID',
};

const getInitialSlideSize = (courseId, classroomConfigs) =>
  classroomConfigs[courseId] ? classroomConfigs[courseId].videoSlideScale : 0.5;

const useResizeVideoSlide = (initialSlideValue) => {
  const [currentSize, setCurrentSize] = useState({
    variant: initialSlideValue === 0.5 ? VARIANTS.EQUAL : VARIANTS.FLUID,
    value: initialSlideValue ?? 0,
  });

  //TODO: [SKOOLDIO-5357] Improve requestAnimationFrame
  const handleResize = useCallback((value) => {
    requestAnimationFrame(() => {
      setCurrentSize({
        ...currentSize,
        variant: value === 0.5 ? VARIANTS.EQUAL : VARIANTS.FLUID,
        value,
      });
    });
  }, []);

  return [currentSize, handleResize];
};

const LAYOUT_ALIGNMENT_MODE = {
  TOP: 'TOP',
  MIDDLE: 'MIDDLE',
  BOTTOM: 'BOTTOM',
};

const getLayoutAlignment = (alignment = LAYOUT_ALIGNMENT_MODE.TOP) => {
  switch (alignment) {
    case LAYOUT_ALIGNMENT_MODE.MIDDLE:
      return 'center';
    case LAYOUT_ALIGNMENT_MODE.BOTTOM:
      return 'flex-end';
    case LAYOUT_ALIGNMENT_MODE.TOP:
    default:
      return 'flex-start';
  }
};

const VideoSlide = ({
  videoAsset,
  slideAsset,
  onGetSecureAssets,
  videoProps,
  onLogAction,
  assetReferId,
}) => {
  const { courseId } = useClassroomContext();
  const [slides, setSlides] = useState(slideAsset);
  const [classroomConfigs, setClassroomConfigs] = useLocalStorage('CLASSROOM_CONFIGS', {});
  const initialSlideValue = getInitialSlideSize(courseId, classroomConfigs);
  const [{ value: sizeValue }, onResize] = useResizeVideoSlide(initialSlideValue);
  const {
    classroom: { enableSlideFullscreenMode },
  } = useConfig();

  const setSlideScaleToLocalStorage = (value) => {
    if (classroomConfigs[courseId]) {
      classroomConfigs[courseId].videoSlideScale = value;
    } else {
      classroomConfigs[courseId] = { videoSlideScale: value };
    }
    setClassroomConfigs(classroomConfigs);
  };

  useEffect(() => {
    const getSlides = async () => {
      const assetList = slideAsset.map(({ slide }) => {
        const { key, bucket } = slide;
        return { key, bucket };
      });
      const assetUrls = await onGetSecureAssets(assetList, assetReferId);
      const slideAssets = assetUrls.map(({ key, signedUrl }) => {
        const baseAsset = slideAsset.find(({ slide }) => key === slide?.key);
        return { url: signedUrl, time: baseAsset.time / 1000 };
      });
      setSlides(slideAssets);
    };
    getSlides();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dataMarkers = slides.map((slide) => ({ time: slide?.time }));
  const videoLayoutAlignment = getLayoutAlignment();

  return (
    <WireSyncProvider
      initialNodes={{
        keys: Object.values(COMPONENT_SYNC_KEY),
        defaultRole: { [COMPONENT_SYNC_KEY.VIDEO]: ROLES.MASTER },
      }}
    >
      <VideoSlideLayout
        alignment={videoLayoutAlignment}
        headerProps={{
          justifyContent: 'flex-end',
          mb: 4,
          px: 4,
        }}
        header={
          <Flex width={'250px'}>
            <Text textStyle={'heading3'} fontWeight={'normal'} color={'greyscale.grey3'}>
              <SvgIcon icon={SvgIcon.ICON_LIST.VIDEO} />
            </Text>
            <Flex width={1} mx={3}>
              <Slider
                value={sizeValue}
                max={1}
                step={0.01}
                valueLabelDisplay="off"
                marks={[
                  { value: 0 },
                  { value: 0.25 },
                  { value: 0.5 },
                  { value: 0.75 },
                  { value: 1 },
                ]}
                track={false}
                onChange={(e, value) => onResize(value)}
                onChangeCommitted={(e, value) => {
                  setSlideScaleToLocalStorage(value);
                  onLogAction(genSliderEvent(SLIDER_ACTIONS.CHANGE, value.toString()));
                }}
              />
            </Flex>
            <Text
              textStyle={'heading3'}
              fontWeight={'normal'}
              color={'greyscale.grey3'}
              fontSize={6}
            >
              <SvgIcon icon={SvgIcon.ICON_LIST.SLIDE} />
            </Text>
          </Flex>
        }
        left={
          <Video
            videoAsset={videoAsset}
            syncKey={COMPONENT_SYNC_KEY.VIDEO}
            sliderValue={sizeValue}
            markers={dataMarkers}
            {...videoProps}
          />
        }
        rightLayoutSize={sizeValue}
        right={
          enableSlideFullscreenMode ? (
            <SlideWithFullScreen
              slides={slides}
              syncKey={COMPONENT_SYNC_KEY.SLIDE}
              sliderValue={sizeValue}
            />
          ) : (
            <Slide slides={slides} syncKey={COMPONENT_SYNC_KEY.SLIDE} sliderValue={sizeValue} />
          )
        }
      />
    </WireSyncProvider>
  );
};

VideoSlide.propTypes = {
  slideAsset: PropTypes.array,
  videoAsset: PropTypes.shape({
    provider: PropTypes.oneOf(Object.values(VIDEO_PROVIDER)),
    videoId: PropTypes.string,
    url: PropTypes.string,
    thumbnailUrl: PropTypes.string,
    duration: PropTypes.number,
    title: PropTypes.string,
    type: PropTypes.oneOf(Object.values(MATERIAL_TYPE)),
  }),
  videoProps: PropTypes.object,
  onGetSecureAssets: PropTypes.func,
  onLogAction: PropTypes.func,
  assetReferId: PropTypes.string.isRequired,
};
VideoSlide.defaultProps = {
  slideAsset: [],
  videoAsset: {},
  videoProps: {},
  onGetSecureAssets: (url) => url,
};

export default withAnalytic(VideoSlide);
